import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  BlockOutlined,
  CheckCircleOutline,
  WarningAmberOutlined,
} from "@mui/icons-material";
import AccountStatusNoticeInfoPanel from "./AccountStatusNoticeInfoPanel";
import {
  LOBBY_REVIEW_PANEL_ANCHOR_ID,
  useLobbyReviewPanelBridge,
} from "./LobbyReviewPanelBridgeContext";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const ACCOUNT_PATH = "/account";
const ACCOUNT_SETUP_PATH = "/settings/account";
export const PLAYSAFE_READY_NOTICE_STORAGE_KEY = "ouro_playsafe_ready_to_queue_seen";
const MOBILE_NAV_MQ = `@media ${MOBILE_NAV_MEDIA}`;

function isSteamSignupUser(user) {
  if (user?.signupSource === "steam") return true;
  const email = String(user?.email || "");
  return email.endsWith("@steam.ouro.local");
}

export function isPlaySafeSignupUser(user) {
  if (user?.signupSource === "playsafe" || user?.playsafeId) return true;
  const email = String(user?.email || "");
  return email.endsWith("@playsafe.ouro.local");
}

/**
 * One-time lobby notice for verified PlaySafe users (dismiss stored in localStorage).
 */
export function getPlaySafeReadyNotice(user) {
  if (!isPlaySafeSignupUser(user)) return null;
  if (user?.accountStatus !== "verified") return null;

  return {
    variant: "card",
    severity: "success",
    title: "You're ready to queue",
    body:
      "You signed in with PlaySafe, so your identity is already verified and you can join matchmaking right away. Optionally complete your profile on the account page to strengthen your trust score.",
    ctaLabel: "Complete profile",
    dismissLabel: "Got it!",
    localStorageKey: PLAYSAFE_READY_NOTICE_STORAGE_KEY,
    fetchReadiness: false,
  };
}

function useNoticeDismissed(storageKey) {
  const [dismissed, setDismissed] = useState(() => {
    if (!storageKey || typeof window === "undefined" || !window.localStorage) {
      return false;
    }
    try {
      return window.localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      // localStorage may be unavailable
    }
    setDismissed(true);
  }, [storageKey]);

  return { dismissed, dismiss };
}

/**
 * Returns a notice config for Steam users who have not yet supplied their
 * real email or legal name. This is checked before the generic accountStatus
 * check so Steam users see a targeted, actionable prompt rather than the
 * generic "complete verification" copy.
 */
export function getSteamCompletionNotice(user) {
  if (!isSteamSignupUser(user)) return null;
  if (!user?.emailIsSynthetic || user?.emailVerifiedAt) return null;

  const hasName = !!(user?.name?.trim());
  const title = "Application pending review";
  const body = "Add an email or enable browser notifications so we can update you when we accept your request.";

  return {
    variant: "card",
    severity: "info",
    title,
    body,
    ctaLabel: "Add email",
    showPushOptIn: true,
    fetchReadiness: false,
    surface: "panel",
  };
}

export function getLobbyAccountStatusNotice(accountStatus, options = {}) {
  const {
    phoneVerificationStatus = null,
    bannedUntilDisplayDate = "",
  } = options;

  if (accountStatus === "pending_steam_link") {
    return {
      variant: "warningBanner",
      severity: "warning",
      title: "Connect your Steam account to continue",
      body:
        "PlaySafe ID needs your Steam account linked before we can confirm your identity and enable matchmaking.",
      ctaLabel: "Connect Steam",
      ctaHref: "/auth/playsafe/connect-steam?steam_link=session",
    };
  }
  if (accountStatus === "pending") {
    return {
      variant: "card",
      severity: "info",
      title: "Verify to unlock matchmaking",
      body:
        "Finish account setup to enable matchmaking.",
      subMessage: "Have a FaceIT profile? Link it to carry over your ELO.",
      ctaLabel: "Complete account setup",
      accountPath: ACCOUNT_SETUP_PATH,
      fetchReadiness: true,
      surface: "panel",
    };
  }
  if (accountStatus === "pending_review") {
    if (phoneVerificationStatus === "phone_verification_ready") {
      return {
        variant: "card",
        severity: "success",
        title: "Optional: verify your phone number",
        body:
          "Your profile is under review. Phone verification is optional, but completing it can increase your trust factor and help reviewers build confidence faster.",
        ctaLabel: "Verify phone",
        dismissLabel: "Skip",
        fetchReadiness: true,
      };
    }

    if (phoneVerificationStatus === "code_sent") {
      return {
        variant: "card",
        severity: "info",
        title: "Enter your SMS verification code",
        body:
          "We sent a one-time code to your phone. Open your account page and enter it to complete optional phone verification.",
        ctaLabel: "Enter code on account",
        fetchReadiness: true,
        surface: "panel",
      };
    }

    if (phoneVerificationStatus === "expired") {
      return {
        variant: "warningBanner",
        severity: "warning",
        title: "Your SMS code expired",
        body:
          "The verification code is no longer valid. Head to your account settings and click \"Send Verification Code\" to request a new one.",
        ctaLabel: "Go to settings",
        fetchReadiness: false,
      };
    }

    return {
      variant: "card",
      severity: "info",
      title: "Profile under review",
      body:
        "Your profile is under review. Most players are verified within a few hours — we'll send you an update when done.",
      ctaLabel: null,
      fetchReadiness: true,
      surface: "panel",
    };
  }
  if (accountStatus === "rejected") {
    return {
      variant: "warningBanner",
      severity: "warning",
      title: "Your verification needs attention",
      body:
        "Our details couldn't be approved. Check your account settings and make sure your Steam profile and inventory are visible.",
      ctaLabel: "Review settings",
    };
  }
  if (accountStatus === "noteligible") {
    return {
      variant: "statusBanner",
      severity: "error",
      title: "Not eligible to queue right now",
      body:
        "You are not eligible right now. We'll email you if this changes.",
      ctaLabel: null,
    };
  }
  if (accountStatus === "banned") {
    return {
      variant: "statusBanner",
      severity: "error",
      title: "Matchmaking access suspended",
      body: bannedUntilDisplayDate
        ? `You're currently banned from Ouro matchmaking until ${bannedUntilDisplayDate}.`
        : "You're currently banned from Ouro matchmaking.",
      ctaLabel: null,
    };
  }
  return null;
}

function StatusInlineBanner({ notice, onDismiss }) {
  const isWarning = notice.severity === "warning";
  const isError = notice.severity === "error";
  const accentColor = isWarning
    ? "warning.main"
    : isError
      ? "error.main"
      : "success.main";
  const buttonColor = isWarning ? "warning" : isError ? "error" : "success";
  const LeadingIcon = isWarning
    ? WarningAmberOutlined
    : isError
      ? BlockOutlined
      : CheckCircleOutline;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        mb: { xs: "1rem", xl: "1.15rem" },
        p: 2,
        borderRadius: "16px",
        border: "1px solid rgba(255,255,255,0.08)",
        borderLeft: `4px solid ${theme.palette[isWarning ? "warning" : isError ? "error" : "success"].main}`,
      })}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          [MOBILE_NAV_MQ]: {
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.5,
          },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LeadingIcon sx={{ color: accentColor, fontSize: "1.25rem" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
              {notice.title}
            </Typography>
          </Box>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.35, ml: "calc(1.25rem + 8px)", fontSize: "0.85rem" }}
          >
            {notice.body}
          </Typography>
        </Box>
        {(notice.ctaLabel || (notice.dismissLabel && onDismiss)) ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              [MOBILE_NAV_MQ]: {
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
                pl: "calc(1.25rem + 8px)",
              },
            }}
          >
            {notice.ctaLabel ? (
              <Button
                component={Link}
                to={notice.ctaHref || ACCOUNT_PATH}
                variant="outlined"
                color={buttonColor}
                size="large"
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  [MOBILE_NAV_MQ]: { width: "100%" },
                }}
              >
                {notice.ctaLabel}
              </Button>
            ) : null}
            {notice.dismissLabel && onDismiss ? (
              <Button
                variant="text"
                color={buttonColor}
                size="large"
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  color: "text.secondary",
                  border: "0 !important",
                  boxShadow: "none !important",
                  backgroundColor: "transparent",
                  minWidth: "auto",
                  px: 0.75,
                  [MOBILE_NAV_MQ]: {
                    width: "100%",
                    justifyContent: "center",
                  },
                  "&:hover": {
                    color: accentColor,
                    textDecoration: "underline",
                    border: "0 !important",
                    boxShadow: "none !important",
                    backgroundColor: "transparent",
                  },
                }}
                onClick={onDismiss}
              >
                {notice.dismissLabel}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
}

export function LobbyAccountStatusNotice({
  accountStatus,
  phoneVerificationStatus = null,
  user = null,
}) {
  const {
    setPendingReviewPanelActive,
    isPendingReviewPanelOpen,
    closePendingReviewPanel,
  } = useLobbyReviewPanelBridge();
  const playSafeReady = getPlaySafeReadyNotice(user);
  const { dismissed: playSafeDismissed, dismiss: dismissPlaySafeNotice } =
    useNoticeDismissed(playSafeReady?.localStorageKey);
  const providerNotice =
    (playSafeReady && !playSafeDismissed ? playSafeReady : null) ||
    getSteamCompletionNotice(user);
  const notice = providerNotice || getLobbyAccountStatusNotice(accountStatus, {
    phoneVerificationStatus,
    bannedUntilDisplayDate: user?.bannedUntilDisplayDate || "",
  });
  const isPanelNotice = notice?.surface === "panel";

  useEffect(() => {
    setPendingReviewPanelActive(isPanelNotice);
  }, [isPanelNotice, setPendingReviewPanelActive]);

  if (!notice) {
    return null;
  }

  if (isPanelNotice) {
    if (!isPendingReviewPanelOpen || typeof document === "undefined") {
      return null;
    }

    const panelAnchor = document.getElementById(LOBBY_REVIEW_PANEL_ANCHOR_ID);
    if (!panelAnchor) {
      return null;
    }

    return ReactDOM.createPortal(
      <AccountStatusNoticeInfoPanel
        notice={notice}
        onClose={closePendingReviewPanel}
        user={user}
        accountPath={notice.accountPath || ACCOUNT_PATH}
      />,
      panelAnchor
    );
  }

  return (
    <StatusInlineBanner
      notice={notice}
      onDismiss={notice.dismissLabel ? dismissPlaySafeNotice : undefined}
    />
  );
}
